/* eslint-disable max-lines */
/**
 * Entities Api Actions
 */

import { NgRedux } from '@angular-redux/store';

import { Injectable } from '@angular/core';
import {
  IAction,
  IBaseEntityType,
  IDocumentOperationOptions,
  ENTITY_TYPES,
  IFilterQuery,
  IOpenRouteOptions,
  ObjectId,
} from '@shared/types';
import { generateId } from '@shared/utils';
import {
  difference,
  get,
  isEmpty,
  isEqual,
  keys,
  pick,
  reduce,
  uniq,
} from 'lodash';
import { RouterActions } from '../router';
import { IAppState } from '../../reducers';
import { EntityServiceProvider } from '@shared/services/datasource';
import {
  BULK_UPDATE,
  BULK_UPDATE_ERROR,
  BULK_UPDATE_SUCCESS,
  CLEAR_CURRENT_DOCUMENT,
  DELETE_DOCUMENT_BY_ENTITY_ID,
  DELETE_DOCUMENT_BY_ENTITY_ID_ERROR,
  DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS,
  FETCH_CURRENT_LIST,
  FETCH_GET_DOCUMENT_BY_ID,
  FETCH_GET_DOCUMENT_BY_ID_ERROR,
  FETCH_GET_DOCUMENT_BY_ID_SUCCESS,
  FETCH_LIST,
  FETCH_LIST_ERROR,
  FETCH_LIST_SUCCESS,
  FETCH_SCHEMA,
  FETCH_SCHEMA_ERROR,
  FETCH_SCHEMA_SUCCESS,
  FETCH_CREATE_ENTITY,
  FETCH_CREATE_ENTITY_ERROR,
  FETCH_CREATE_ENTITY_SUCCESS,
  FETCH_UPDATE_ENTITY,
  FETCH_UPDATE_ENTITY_SUCCESS,
  FETCH_UPDATE_ENTITY_ERROR,
  FORM_STATUS_CHANGED,
  FORM_VALUE_CHANGED,
  GET_ENTITY_CAPTIONS_SUCCESS,
  SHOW_DOCUMENT,
  STORE_CLEAR_ITEMS,
  STORE_ORDER_STATUS,
  STORE_UPDATE_DATA,
  REFERENCE_DATA_REQUIRED,
  STORE_UPDATE_ITEM,
} from '../base';
import { IBaseServiceClass } from '@shared/services/base/base.service';

import { StateService } from '@shared/services/state-service';
import { BaseApiActions } from '../base/base.api.actions';

const storeUpdateData: (data: any) => IAction = (data): any => ({
  type: STORE_UPDATE_DATA,
  payload: {
    data,
  },
});

@Injectable()
export class APIActions extends BaseApiActions {
  public requestEntitiesCaptionsTimeout: NodeJS.Timer;
  public entitiesIds: Record<string, string[]> = {};

  public constructor(
    public ngRedux: NgRedux<IAppState>,
    public entityServiceProvider: EntityServiceProvider,
    public stateService: StateService,
  ) {
    super(ngRedux);
  }

  /**
   * Actions creator for fetchList error response
   *
   * @param entityType: string,
   * @param errors - errors
   * @param requestPayload - response object
   * @param options - document operation options
   * @returns
   */
  public fetchListErrorAction(
    entityType: string,
    errors,
    requestPayload,
    options?: IDocumentOperationOptions,
  ): IAction {
    return {
      type: FETCH_LIST_ERROR,
      payload: {
        errors,
        options,
        request: requestPayload,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for bulkUpdate error response
   *
   * @param entityType: string,
   * @param errors - errors
   * @param requestPayload - response object
   * @param options - document operation options
   * @returns
   */
  public bulkUpdateErrorAction(
    entityType: string,
    errors,
    requestPayload,
    options?: IDocumentOperationOptions,
  ): IAction {
    return {
      type: BULK_UPDATE_ERROR,
      payload: {
        errors,
        options,
        request: requestPayload,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for formStatusChanged
   *
   * @param entityType: string
   * @param formStatus related form properties
   * @returns
   */
  public formStatusChangedAction(
    entityType: string,
    formStatus: object,
  ): IAction {
    return {
      type: FORM_STATUS_CHANGED,
      payload: {
        formStatus,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for formValueChanged
   *
   * @param entityType: string
   * @param formValue - new form value
   * @param documentIds
   * @returns
   */
  public formValueChangedAction(
    entityType: string,
    formValue,
    documentIds: string[],
  ): IAction {
    return {
      type: FORM_VALUE_CHANGED,
      payload: {
        documentIds,
        formValue,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for createEntity
   *
   * @param entityType: string
   * @param documents - documents
   * @param options - Document Options
   * @returns
   */
  public createEntityAction(
    entityType: string,
    documents,
    options: any,
  ): IAction {
    return {
      type: FETCH_CREATE_ENTITY,
      payload: {
        documents,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for updateEntity
   *
   * @param entityType: string
   * @param documents - documents
   * @param options - Document Options
   * @returns
   */
  public updateEntityAction(
    entityType: string,
    documents,
    options: any,
  ): IAction {
    return {
      type: FETCH_UPDATE_ENTITY,
      payload: {
        documents,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for CLEAR_CURRENT_DOCUMENT
   *
   * @param entityType: string
   * @returns
   */
  public clearCurrentDocumentAction(entityType: string): IAction {
    return {
      type: CLEAR_CURRENT_DOCUMENT,
      payload: {
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for getSchema
   *
   * @param entityType: string
   * @returns
   */
  public fetchSchemaAction(entityType: string): IAction {
    return {
      type: FETCH_SCHEMA,
      payload: { entity_type: entityType },
    };
  }

  /**
   * Actions creator for fetchCurrentList
   *
   * @param entityType: string
   * @param query - query object
   * @param options: IDocumentOperationOptions
   * @returns
   */
  public fetchCurrentListAction(
    entityType: string,
    query: IFilterQuery = null,
    options: IDocumentOperationOptions = {},
  ): IAction {
    return {
      type: FETCH_CURRENT_LIST,
      payload: {
        query,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for showDocument
   *
   * @param entityType: string
   * @param document - document object
   * @returns
   */
  public showDocumentAction(entityType: string, document: Object): IAction {
    return {
      type: SHOW_DOCUMENT,
      payload: {
        document,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for deleteDocumentByEntityIdAction
   *
   * @param entityType: string
   * @param entityId - product entity id
   * @param options - action options
   * @returns
   */
  public deleteDocumentByEntityIdAction(
    entityType: ENTITY_TYPES,
    entityId,
    options: IDocumentOperationOptions,
  ): IAction {
    return {
      type: DELETE_DOCUMENT_BY_ENTITY_ID,
      payload: {
        entityId,
        options,
        entity_type: entityType,
      },
    };
  }

  public deleteDocumentByEntityIdSuccessAction(
    entityType: ENTITY_TYPES,
    response,
    options: IDocumentOperationOptions,
  ): IAction {
    return {
      type: DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS,
      payload: {
        response,
        options,
        entity_type: entityType,
      },
    };
  }

  public deleteDocumentByEntityIdErrorAction(
    entityType: ENTITY_TYPES,
    errors,
    options?: IDocumentOperationOptions,
  ): IAction {
    return {
      type: DELETE_DOCUMENT_BY_ENTITY_ID_ERROR,
      payload: {
        errors,
        options,
        entity_type: entityType,
      },
    };
  }
  /**
   * Method for dispatching referenceDataRequired action
   */
  public referenceDataRequired(
    entityTypes: string[],
    entityType: string,
  ): void {
    this.ngRedux.dispatch(
      this.referenceDataRequiredAction(entityTypes, entityType),
    );
  }

  /**
   * Actions creator for referenceDataRequired
   *
   * @param entityTypes: string[]
   * @param entityType: string
   * @returns
   */
  public referenceDataRequiredAction(
    entityTypes: string[],
    entityType: string,
  ): IAction {
    return {
      type: REFERENCE_DATA_REQUIRED,
      payload: {
        entityTypes,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for fetchGetDocumentById
   *
   * @param entityType: string
   * @param id: ID string
   * @param options
   * @returns
   */
  public fetchGetDocumentByIdAction(
    entityType: string,
    id: string,
    options: IDocumentOperationOptions,
  ): IAction {
    return {
      type: FETCH_GET_DOCUMENT_BY_ID,
      payload: {
        id,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for fetchList success response
   *
   * @param entityType: string
   * @param response - response object
   * @param options - DocumentOperationOptions
   * @returns
   */
  public fetchGetDocumentByIdSuccessAction(
    entityType: string,
    response,
    options: IDocumentOperationOptions,
  ): IAction {
    return {
      type: FETCH_GET_DOCUMENT_BY_ID_SUCCESS,
      payload: {
        response,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for fetchSchema success response
   *
   * @param entityType: string,
   * @param response - response object
   * @returns
   */
  public fetchSchemaSuccessAction(entityType: string, response): IAction {
    return {
      type: FETCH_SCHEMA_SUCCESS,
      payload: {
        response,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for fetchSchema error response
   *
   * @param entityType: string,
   * @param errors - errors
   * @returns
   */
  public fetchSchemaErrorAction(entityType: string, errors): IAction {
    return {
      type: FETCH_SCHEMA_ERROR,
      payload: {
        errors,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for fetchList success response
   *
   * @param entityType: string,
   * @param response - response object
   * @param requestPayload - response object
   * @param options - response object
   * @returns
   */
  public fetchListSuccessAction(
    entityType: string,
    response,
    requestPayload,
    options?: IDocumentOperationOptions,
  ): IAction {
    return {
      type: FETCH_LIST_SUCCESS,
      payload: {
        response,
        options,
        request: requestPayload,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for bulkUpdate success response
   *
   * @param entityType: string,
   * @param response - response object
   * @param requestPayload - response object
   * @param options - response object
   * @returns
   */
  public bulkUpdateSuccessAction(
    entityType: string,
    response,
    requestPayload,
    options?: IDocumentOperationOptions,
  ): IAction {
    return {
      type: BULK_UPDATE_SUCCESS,
      payload: {
        response,
        options,
        request: requestPayload,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for createEntity success response
   *
   * @param entityType: string
   * @param response - response object
   * @returns
   */
  public createEntitySuccessAction(
    entityType: string,
    response,
    options,
  ): IAction {
    return {
      type: FETCH_CREATE_ENTITY_SUCCESS,
      payload: {
        response,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for createEntity error response
   *
   * @param entityType: string
   * @param errors - errors
   * @param options
   * @returns
   */
  public createEntityErrorAction(entityType: string, errors, options): IAction {
    return {
      type: FETCH_CREATE_ENTITY_ERROR,
      payload: {
        errors,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for updateEntity success response
   *
   * @param entityType: string
   * @param response - response object
   * @param options - response object
   * @returns
   */
  public updateEntitySuccessAction(
    entityType: string,
    response,
    options,
  ): IAction {
    return {
      type: FETCH_UPDATE_ENTITY_SUCCESS,
      payload: {
        response,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for updateEntity error response
   *
   * @param entityType: string
   * @param errors - errors
   * @param options - options
   * @returns
   */
  public updateEntityErrorAction(entityType: string, errors, options): IAction {
    return {
      type: FETCH_UPDATE_ENTITY_ERROR,
      payload: {
        errors,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for storeClearItems
   *
   * @param entityType: string
   * @returns
   */
  public storeClearItemsAction(entityType: string): IAction {
    return {
      type: STORE_CLEAR_ITEMS,
      payload: {
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for getEntityCaptions success response
   *
   * @param entityType: string,
   * @param itemCaptions - captions object
   * @returns
   */
  public getEntityCaptionsSuccessAction(
    entityType: string,
    itemCaptions: object,
  ): IAction {
    return {
      type: GET_ENTITY_CAPTIONS_SUCCESS,
      payload: {
        itemCaptions,
        entity_type: entityType,
      },
    };
  }

  public requestEntitiesCaptions(entityType: string, ids: string[]): void {
    clearTimeout(this.requestEntitiesCaptionsTimeout);

    const store = this.ngRedux.getState();
    const entityIdsInStore = keys(
      get(store, `entities.${entityType}.itemCaptions`, {}),
    );

    if (!this.entitiesIds[entityType]) {
      this.entitiesIds[entityType] = [];
    }

    this.entitiesIds[entityType] = difference(
      uniq(this.entitiesIds[entityType].concat(ids)),
      entityIdsInStore,
    );
  }

  /**
   * Method for dispatching clearCurrentDocumentAction action
   */
  public clearCurrentDocument(entityType: string): void {
    this.ngRedux.dispatch(this.clearCurrentDocumentAction(entityType));
  }

  /**
   * Method for dispatching fetchSchema action
   */
  public fetchSchema(entityType: string): void {
    this.ngRedux.dispatch(this.fetchSchemaAction(entityType));
  }

  /**
   * Method for dispatching fetchSchema action if no schema can be found in store
   *
   * @param entityType: string
   */
  public ensureEntitySchemaLoaded(entityType: string): void {
    if (!entityType) {
      return;
    }
    const state: IAppState = this.ngRedux.getState();
    const isSchemaRequested: boolean = get(state, [
      'entities',
      entityType,
      'isSchemaRequested',
    ]);
    // TODO: loader indicator should be hidden only when all active requests are settled.
    // now it hides on the first success or fail.
    if (!isSchemaRequested) {
      this.fetchSchema(entityType);
    }
  }

  /**
   * Method for dispatching fetchCurrentList action
   *
   * @param entityType: string
   * @param query - query object
   */
  public fetchCurrentList(
    entityType: string,
    query: IFilterQuery = {},
    options: IDocumentOperationOptions = {},
  ): Promise<any> {
    const reqOptions: any = Object.assign({ requestId: generateId() }, options);
    this.ngRedux.dispatch(
      this.fetchCurrentListAction(entityType, query, reqOptions),
    );

    return this.resultAsPromised(reqOptions.requestxId);
  }

  /**
   * Method for dispatching showDocumentAction action
   *
   * @param entityType: string
   * @param document - document object
   */
  public showDocument(entityType: string, document: Object = null): void {
    this.ngRedux.dispatch(this.showDocumentAction(entityType, document));
  }

  /**
   * Actions creator for fetchGetDocumentById error response
   *
   * @param entityType: string
   * @param errors - errors
   * @param options - request options
   * @returns
   */
  public fetchGetDocumentByIdErrorAction(
    entityType: string,
    errors,
    options: IDocumentOperationOptions,
  ): IAction {
    return {
      type: FETCH_GET_DOCUMENT_BY_ID_ERROR,
      payload: {
        errors,
        options,
        entity_type: entityType,
      },
    };
  }

  // helper adds entity_type into payload and dispatches action with ngRedux.dispatch
  public dispatch(entityType: string, action): any {
    const payload: object | string =
      typeof action.payload === 'string'
        ? action.payload
        : Object.assign({ entity_type: entityType }, action.payload);

    return this.ngRedux.dispatch(Object.assign({}, action, { payload }));
  }

  /**
   * Method for dispatching deleteDocumentByEntityId action
   *
   * @param entityType
   * @param entityId - product entity id
   * @param options - options used for request
   */
  public deleteDocumentByEntityId(
    entityType: ENTITY_TYPES,
    entityId: ObjectId,
    options: IDocumentOperationOptions = {},
  ): Promise<any> {
    const reqOptions: any = Object.assign({ requestId: generateId() }, options);

    this.ngRedux.dispatch(
      this.deleteDocumentByEntityIdAction(entityType, entityId, reqOptions),
    );

    return this.resultAsPromised(reqOptions.requestId);
  }

  public storeUpdateData(entityType: string, payload): void {
    this.dispatch(entityType, storeUpdateData(payload));
  }

  public formStatusChanged(entityType: string, formStatus): void {
    const prevStatus: object = pick(
      this.getFromStore(['entities', entityType, 'formStatus']),
      'valid',
      'touched',
      'status',
    );
    if (isEqual(formStatus, prevStatus)) return;
    this.dispatch(
      entityType,
      this.formStatusChangedAction(entityType, formStatus),
    );
  }

  public formValueChanged(
    entityType: string,
    formValue,
    documentIds: string[] = [],
  ): void {
    this.dispatch(
      entityType,
      this.formValueChangedAction(entityType, formValue, documentIds),
    );
  }

  public openDocumentEditor(
    entityType: string,
    id: string,
    options?: IOpenRouteOptions,
    params?: {},
  ): void {
    const service: IBaseServiceClass =
      this.entityServiceProvider.getServiceClass(entityType);
    const uiRoute: string = get(service, 'config.uiRoute', entityType);
    const enableEntryDuplicate: string = get(
      service,
      'config.editor.enableEntryDuplicate',
      false,
    );

    const combinedParams = {
      ...(enableEntryDuplicate && { showDuplicate: true }),
      ...params,
    };

    let url = '';
    if (!isEmpty(combinedParams)) {
      url = reduce(
        combinedParams,
        (urls: string[], value, key) => {
          urls.push(`${key}=${value}`);

          return urls;
        },
        [],
      ).join('&');
    }

    this.ngRedux.dispatch(
      RouterActions.updateLocationAction(`${uiRoute}/${id}?${url}`, options),
    );
  }

  public openList(entityType: string): void {
    const service: IBaseServiceClass =
      this.entityServiceProvider.getServiceClass(entityType);
    const uiRoute: string = get(service, 'config.uiRoute', entityType);
    this.ngRedux.dispatch(RouterActions.updateLocationAction(uiRoute));
  }

  /**
   * Helper
   *
   * @param propertyPath
   * @param defaultValue
   */
  public getFromStore(
    propertyPath: string | string[],
    defaultValue?: any,
  ): any {
    const state: IAppState = this.ngRedux.getState();

    return get(state, propertyPath, defaultValue);
  }

  /**
   * Method for dispatching storeClearItems action
   *
   * @param entityType: string
   */
  public storeClearItems(entityType: string): void {
    this.ngRedux.dispatch(this.storeClearItemsAction(entityType));
  }

  /**
   * Method for dispatching createEntityAction action
   *
   * @param entityType: string
   * @param documents - array of document objects
   * @param userOptions
   */
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public createEntityAsPromised(
    entityType: string,
    documents: IBaseEntityType | IBaseEntityType[],
    userOptions: IDocumentOperationOptions = {},
  ): Promise<any> {
    const options: any = Object.assign(
      { requestId: generateId() },
      userOptions,
    );
    this.ngRedux.dispatch(
      this.createEntityAction(entityType, documents, options),
    );

    return this.resultAsPromised(options.requestId);
  }

  /**
   * Method for dispatching updateEntityAction action
   *
   * @param entityType: string
   * @param documents - array of document objects
   * @param userOptions
   */
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public updateEntityAsPromised(
    entityType: string,
    documents: IBaseEntityType | IBaseEntityType[],
    userOptions: IDocumentOperationOptions = {},
  ): Promise<any> {
    const options: any = Object.assign(
      { requestId: generateId() },
      userOptions,
    );
    this.ngRedux.dispatch(
      this.updateEntityAction(entityType, documents, options),
    );

    return this.resultAsPromised(options.requestId);
  }

  /**
   * Method for dispatching fetchGetDocumentById action
   *
   * @param entityType: string
   * @param id - ID string
   * @param options - IDocumentOperationOptions
   * @returns Promise<any>
   */
  public fetchGetDocumentById(
    entityType: string,
    id: string,
    options: IDocumentOperationOptions = {},
  ): Promise<any> {
    const reqOptions = Object.assign({ requestId: generateId() }, options);
    this.ngRedux.dispatch(
      this.fetchGetDocumentByIdAction(entityType, id, reqOptions),
    );

    return this.resultAsPromised(reqOptions.requestId);
  }

  /**
   * Method for dispatching fetchList action
   *
   * @param entityType: string
   * @param query - query object
   * @param options - DocumentOperationOptions
   */
  public fetchList(
    entityType: string,
    query: IFilterQuery = {},
    options: IDocumentOperationOptions = {},
  ): Promise<any> {
    const reqOptions: any = Object.assign({ requestId: generateId() }, options);

    this.ngRedux.dispatch(this.fetchListAction(entityType, query, reqOptions));

    return this.resultAsPromised(reqOptions.requestId);
  }

  /**
   * Actions creator for fetchList
   *
   * @param entityType: string
   * @param query - query object
   * @param options - DocumentOperationOptions
   * @returns
   */
  public fetchListAction(
    entityType: string,
    query: IFilterQuery = null,
    options?: IDocumentOperationOptions,
  ): IAction {
    return {
      type: FETCH_LIST,
      payload: {
        query,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Method for dispatching bulkUpdate action
   *
   * @param entityType: string
   * @param documents - bulk update documents
   * @param options - DocumentOperationOptions
   */
  public bulkUpdate(
    entityType: string,
    documents: IBaseEntityType[],
    options: IDocumentOperationOptions = {},
  ): Promise<any> {
    const reqOptions: any = Object.assign({ requestId: generateId() }, options);

    this.ngRedux.dispatch(
      this.bulkUpdateAction(entityType, documents, reqOptions),
    );

    return this.resultAsPromised(reqOptions.requestId);
  }

  /**
   * Actions creator for bulkUpdate
   *
   * @param entityType: string
   * @param documents - bulk update documents
   * @param options - DocumentOperationOptions
   * @returns
   */
  public bulkUpdateAction(
    entityType: string,
    documents: IBaseEntityType[],
    options?: IDocumentOperationOptions,
  ): IAction {
    return {
      type: BULK_UPDATE,
      payload: {
        documents,
        options,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for storeOrderStatusAction
   *
   * @param entityType: string
   * @param status: string
   * @returns
   */
  public storeOrderStatusAction(
    entityType: ENTITY_TYPES,
    status: string,
  ): IAction {
    // Store the current status in the store
    return {
      type: STORE_ORDER_STATUS,
      payload: {
        status,
        entity_type: entityType,
      },
    };
  }

  /**
   * Actions creator for storeUpdatedItemAction
   *
   * @param entityType: string,
   * @param entityItem - entity object
   * @returns
   */
  public storeUpdatedItemAction(
    entityType: ENTITY_TYPES,
    entityItem: IBaseEntityType,
    options = {},
  ): IAction {
    return {
      type: STORE_UPDATE_ITEM,
      payload: {
        entityItem,
        options,
        entity_type: entityType,
      },
    };
  }

  public storeOrderStatus(entityType: ENTITY_TYPES, status: string): void {
    this.ngRedux.dispatch(this.storeOrderStatusAction(entityType, status));
  }

  public openAuditTrail(entityType: string, id): void {
    const service: IBaseServiceClass =
      this.entityServiceProvider.getServiceClass(entityType);
    const uiRoute: string = this.provideNewRoute(service, entityType);
    this.ngRedux.dispatch(
      RouterActions.updateLocationAction(`${uiRoute}/${id}/history`),
    );
  }

  public provideNewRoute(
    service: IBaseServiceClass,
    entityType: string,
  ): string {
    const replaceUiRoute = get(service, 'config.replaceUiRoute', '');
    const currentUrl = get(
      this,
      'route.snapshot.firstChild.url[0].path',
      entityType,
    );
    if (!!replaceUiRoute && currentUrl === replaceUiRoute) {
      return replaceUiRoute;
    }

    return get(service, 'config.uiRoute', entityType);
  }

  /**
   * Method for dispatching storeUpdatedItemAction action
   *
   * @param entityType - entity type
   * @param document - document object
   */
  public storeUpdatedItem(
    entityType: ENTITY_TYPES,
    document: Object = null,
  ): void {
    this.ngRedux.dispatch(this.storeUpdatedItemAction(entityType, document));
  }

  // eslint-disable-next-line max-lines
}
